import styled from '@emotion/styled'
import { UntitledIcon } from '@faceup/icons'
import { usAttachment02 } from '@faceup/icons/usAttachment02'
import { Button, Space } from '@faceup/ui-base'
import { type ReactNode, useContext, useState } from 'react'
import { UiContext } from '../UiProvider/UiProvider'

type ExtendedData = { [key: string]: unknown }

type AttachmentType<D extends ExtendedData = ExtendedData> = {
  id: string
  name: string
  url: string
  mimetype: string
} & D

type Variant = 'default' | 'with-bg'

const variants: Record<Variant, string> = {
  default: '',
  'with-bg': 'max-w-full py-2 px-3 rounded-lg bg-colorFillContent',
}

type Config<D extends ExtendedData> = {
  onDownload: ((payload: AttachmentType<D>) => Promise<boolean>) | null
  renderFileName?: (children: ReactNode, attachment: AttachmentType<D>) => ReactNode
  isDownloadDisabled?: boolean
  variant?: Variant
}

type AttachmentProps<D extends ExtendedData> = {
  attachment: AttachmentType<D>
  config: Config<D>
}

const AttachmentFileName = <D extends ExtendedData>({ attachment, config }: AttachmentProps<D>) => {
  return config.renderFileName ? (
    config.renderFileName(<FileName>{attachment.name}</FileName>, attachment)
  ) : (
    <FileName>{attachment.name}</FileName>
  )
}

const Attachment = <D extends ExtendedData>({ attachment, config }: AttachmentProps<D>) => {
  const [loading, setLoading] = useState(false)
  const { attachmentList } = useContext(UiContext)
  const variant = config.variant ?? 'default'

  return (
    <LimitedSpace align='center' className={variants[variant]}>
      <UntitledIcon icon={usAttachment02} size={18} className='text-colorTextDescription' />
      {config.isDownloadDisabled ? (
        <span>
          <AttachmentFileName attachment={attachment} config={config} />
        </span>
      ) : (
        <Button
          type={config.onDownload ? 'link' : 'text'}
          size='small'
          className='px-0'
          loading={loading}
          onClick={
            config.onDownload
              ? async () => {
                  setLoading(true)
                  await config.onDownload?.(attachment)
                  setLoading(false)
                }
              : undefined
          }
          label={config.onDownload ? (attachmentList?.download ?? 'Download') : undefined}
        >
          <AttachmentFileName attachment={attachment} config={config} />
        </Button>
      )}
    </LimitedSpace>
  )
}

type AttachmentListProps<D extends ExtendedData = ExtendedData> = {
  attachments: AttachmentType<D>[]
  config: Config<D>
}

export const AttachmentList = <D extends ExtendedData = ExtendedData>(
  props: AttachmentListProps<D>
) => {
  if (!props.attachments.length) {
    return null
  }

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {props.attachments.map(attachment => (
        <Attachment key={attachment.url} attachment={attachment} config={props.config} />
      ))}
    </div>
  )
}

const LimitedSpace = styled(Space)`
  max-width: calc(100% - 150px);

  .ant-space-item {
    max-width: 100%;
    display: inline-flex;
  }
`

const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
  font-size: 14px;
  display: block;
`
